import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useRef, useImperativeHandle, useState, useCallback, useMemo, } from 'react';
import { DayPicker } from 'react-day-picker';
import { cs, enUS } from 'date-fns/locale';
import { useControllableProp, useDropdown } from '../../hooks';
import './Calendar.scss';
import { Popup } from '../popup/Popup';
import { TertiaryBtn } from '../btn/TertiaryBtn';
import { SvgCalendar } from '../../icons/calendar';
import { CalendarHeader } from './CalendarHeader';
import { Input } from '../input/Input';
import { LOCALE, PLACEHOLDERS } from './constants';
import { FORMAT_DATE, VALIDATE_DATE, getLanguage, isDateValid } from './utils';
const getValueAsString = (value, prevValue) => {
    if (value === null) {
        return '';
    }
    if (isDateValid(value)) {
        return FORMAT_DATE[datePickerLang](value);
    }
    return prevValue;
};
const datePickerLang = getLanguage(window.navigator.language || '');
export const DatePicker = forwardRef(({ isDisabled, isRequired, isInvalid, name, defaultValue, value, hasClearBtn, minDay = new Date(1800, 0, 1), maxDay = new Date(2099, 11, 31), onChange, testId, }, ref) => {
    const popupTriggerRef = useRef(null);
    const popupContentRef = useRef(null);
    const inputRef = useRef(null);
    const prevInputValueRef = useRef('');
    const [inputValue, setInputValue] = useControllableProp({
        value: value !== undefined ? getValueAsString(value, prevInputValueRef.current) : undefined,
        defaultValue: defaultValue !== undefined ? getValueAsString(defaultValue, prevInputValueRef.current) : '',
    });
    const selectedDay = VALIDATE_DATE[datePickerLang](inputValue, minDay, maxDay);
    const popupPosition = [
        'bottom-right',
        'bottom-center',
        'bottom-left',
        'top-right',
        'top-center',
        'top-left',
    ];
    const onDropdownHide = useCallback(() => {
        setMonth(undefined);
    }, []);
    const [isDropdownShown, setDropdownShown, handleDropdownKeyDown] = useDropdown(popupTriggerRef, popupContentRef, onDropdownHide);
    const [month, setMonth] = useState(undefined);
    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        },
    }));
    const handleInputChange = (e, eventData) => {
        const date = VALIDATE_DATE[datePickerLang](eventData.value, minDay, maxDay);
        setInputValue?.(eventData.value);
        prevInputValueRef.current = eventData.value;
        onChange(e, {
            name,
            value: date ? date : eventData.value === '' ? null : new Date('Invalid date'),
        });
    };
    const handleInputClick = () => {
        setDropdownShown(!isDropdownShown);
    };
    const handleDayClick = (date, _, e) => {
        setInputValue?.(FORMAT_DATE[datePickerLang](date));
        onChange(e, {
            name,
            value: new Date(date.setHours(0, 0, 0, 0)),
        });
        setDropdownShown(false);
    };
    const handleMonthChange = (month) => {
        setMonth(month);
    };
    const handleButtonClick = () => {
        inputRef.current?.focus();
        setDropdownShown(!isDropdownShown);
    };
    const Caption = useMemo(() => (props) => (_jsx(CalendarHeader, { captionProps: props, lang: datePickerLang, testId: testId ? `${testId}-calendarHeader` : undefined })), [testId]);
    return (_jsxs("div", { className: 'sg-a-d-f sg-a-jc-s', ref: popupTriggerRef, children: [_jsx(Input, { ref: inputRef, name: name, value: inputValue, placeholder: PLACEHOLDERS[datePickerLang], hasClearBtn: hasClearBtn, isDisabled: isDisabled, isRequired: isRequired, isInvalid: isInvalid, isActive: isDropdownShown, autoComplete: 'off', onChange: handleInputChange, onClick: handleInputClick, onKeyDown: handleDropdownKeyDown, testId: testId ? `${testId}-input` : undefined, children: !isDisabled && (_jsx(TertiaryBtn, { icon: { element: _jsx(SvgCalendar, {}) }, onClick: handleButtonClick })) }), isDropdownShown && (_jsx(Popup, { position: popupPosition, triggerRef: popupTriggerRef, children: _jsx("div", { ref: popupContentRef, className: 'sg-a-p-1', "data-test-id": testId ? `${testId}-calendar` : undefined, children: _jsx(DayPicker, { locale: datePickerLang === LOCALE.CS ? cs : enUS, selected: selectedDay || undefined, month: month || selectedDay || undefined, fromMonth: minDay, toMonth: maxDay, showOutsideDays: true, disabled: {
                            before: minDay,
                            after: maxDay,
                        }, components: { Caption: Caption }, onDayClick: handleDayClick, onMonthChange: handleMonthChange, classNames: {
                            root: 'sg-calendar',
                            table: 'sg-calendar--body',
                            head_cell: 'sg-calendar--weekday',
                            cell: 'sg-calendar--dayWrapper',
                            day: 'sg-calendar--day',
                            day_today: 'sg-calendar--day-today',
                            day_selected: 'sg-calendar--day-selected',
                            day_outside: 'sg-calendar--day-outside',
                            day_disabled: 'sg-calendar--day-disabled',
                        } }) }) }))] }));
});
//# sourceMappingURL=DatePicker.js.map